import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  getRideStationDurationDetails,
  IRideDto,
  SECONDS_IN_MINUTE,
  useFormats,
} from "@ryde/shared";

import { FramerTooltip } from "../framer-tooltip";
import { StationDurationTooltipContentStyled } from "./station-duration-tooltip.styled";

interface IStationDurationTooltip {
  rideDto: Partial<IRideDto> | undefined;
  stationIndex: number;
  children: ReactNode;
}

export const StationDurationTooltip: FC<IStationDurationTooltip> = ({
  rideDto,
  stationIndex,
  children,
}) => {
  const { t } = useTranslation();
  const { formatTimeWithSecondsNormalized } = useFormats();

  const durationDetails = getRideStationDurationDetails(rideDto, stationIndex);

  if (
    !durationDetails.previousStationTime ||
    !durationDetails.nextStationTime
  ) {
    return <>{children}</>;
  }

  const previousTimeFormatted = formatTimeWithSecondsNormalized(
    durationDetails.previousStationTime,
  );
  const nextTimeFormatted = formatTimeWithSecondsNormalized(
    durationDetails.nextStationTime,
  );

  const tooltipContent = (
    <StationDurationTooltipContentStyled>
      <div>
        <strong>{t("ride.PREVIOUS_STATION_TIME")}:</strong>{" "}
        {previousTimeFormatted}
      </div>
      {!!durationDetails.durationSeconds && (
        <div>
          <strong>{t("ride.DURATION_TO_NEXT_STATION")}:</strong>{" "}
          {durationDetails.durationSeconds}s = {durationDetails.durationMinutes}
          m{durationDetails.durationSeconds % SECONDS_IN_MINUTE}s
        </div>
      )}
      <div>
        <strong>{t("ride.NEXT_STATION_TIME")}:</strong> {nextTimeFormatted}
      </div>
    </StationDurationTooltipContentStyled>
  );

  return (
    <FramerTooltip content={tooltipContent}>
      <>{children}</>
    </FramerTooltip>
  );
};
